import type { ReactNode } from "react"
import SignIn from "@/pages/SignIn/SignIn"
import StatusScreen from "@/components/StatusScreen/StatusScreen"
import { useAuthStore } from "@/store/authStore"
import { selectAuthChecking, selectIsSignedIn } from "@/store/authStore"

/**
 * Gates the facilitator's workspace. Those screens list the customers someone
 * may see and every session run for them, which is not the room's to see.
 *
 * Backed by Firebase Auth, so unlike the tab marker it replaced this is a real
 * boundary — but only half of one. It decides what is RENDERED; the Firestore
 * rules decide what is readable, and they test the same signed-in uid against
 * `customer.facilitatorIds`. A guest who types the address gets the sign-in
 * screen; a signed-in facilitator who types someone else's customer id gets an
 * empty screen and a permission error, because the query never returns it.
 *
 * `checking` renders a wait, not the sign-in screen: Firebase restores a
 * persisted session asynchronously, so answering early would flash "sign in" at
 * a facilitator who already is, on every reload.
 */
export default function FacilitatorOnly({ children }: { children: ReactNode }) {
  const checking = useAuthStore(selectAuthChecking)
  const signedIn = useAuthStore(selectIsSignedIn)

  if (checking) return <StatusScreen eyebrow="AI Design Cards" title="Signing you in." waiting />
  if (!signedIn) return <SignIn />
  return <>{children}</>
}
